import { useMemo } from "react"

import {
    QueryClient,
    type QueryKey,
    type UseBaseQueryOptions,
    type UseQueryResult,
    type UseSuspenseQueryResult,
    useQuery,
    useQueryClient,
    useSuspenseQuery
} from "@tanstack/react-query"
import type { AxiosError } from "axios"
import memoizeOne, { type MemoizedFn } from "memoize-one"

import { isNotFoundError, isUnauthorizedError } from "@/3514/utils"
import { hours, minutes, seconds } from "@/3514/utils/time.utils"
import { type TEmptyAsyncCallback, emptyAsyncCallback } from "@/shared/types/functions"

import {
    EParticipantOnboardingQueryKey as EQueryKey,
    EParticipantOnboardingStep as EStep
} from "./participant.onboarding.api.config"
import adapters from "./participant.onboarding.api.queries.adapters"
import fetchers from "./participant.onboarding.api.queries.fetchers"
import type {
    IParticipantOnboardingCoachStepCoachesQueryDto as ICoachStepCoachesQueryDto,
    IParticipantOnboardingCoachStepCoachesQueryModel as ICoachStepCoachesQueryModel,
    IParticipantOnboardingCoachStepPairingModel as ICoachStepPairingModel,
    IParticipantOnboardingCoachStepPairingQueryDto as ICoachStepPairingQueryDto,
    IParticipantOnboardingCoachStepSessionQueryDto as ICoachStepSessionQueryDto,
    IParticipantOnboardingCoachStepSessionQueryModel as ICoachStepSessionQueryModel,
    IParticipantOnboardingDiagnosticStepQueryDto as IDiagnosticStepQueryDto,
    IParticipantOnboardingDiagnosticStepQueryModel as IDiagnosticStepQueryModel,
    IParticipantOnboardingHoldsStepQueryDto as IHoldsStepQueryDto,
    IParticipantOnboardingHoldsStepQueryModel as IHoldsStepQueryModel,
    IParticipantOnboardingReflectionStepQueryDto as IReflectionStepQueryDto,
    IParticipantOnboardingReflectionStepQueryModel as IReflectionStepQueryModel,
    IParticipantOnboardingStepsInfoQueryDto as IStepInfoQueryDto,
    IParticipantOnboardingStepsInfoQueryModel as IStepInfoQueryModel,
    IParticipantOnboardingVideoStepQueryDto as IVideoStepQueryDto,
    IParticipantOnboardingVideoStepQueryModel as IVideoStepQueryModel
} from "./participant.onboarding.api.types"

// steps info --start
type TStepInfoQuery = UseQueryResult<IStepInfoQueryModel, AxiosError>
type TStepInfoOptions = Pick<UseBaseQueryOptions, "enabled">
function useStepInfoQuery({ enabled }: TStepInfoOptions): ReturnType<(options: TStepInfoOptions) => TStepInfoQuery> {
    return useQuery<IStepInfoQueryDto, AxiosError, IStepInfoQueryModel, QueryKey>({
        queryKey: EQueryKey.StepInfo(),
        queryFn: fetchers.stepInfo,
        select: adapters.stepInfo,
        staleTime: hours(0.5),
        gcTime: Infinity,
        retry: (failureCount: number, error: AxiosError): boolean =>
            isUnauthorizedError(error) ? false : failureCount < 1,
        retryDelay: seconds(5),
        refetchOnReconnect: true,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        throwOnError: false,
        networkMode: "online",
        notifyOnChangeProps: ["data", "isSuccess", "refetch"],
        enabled
    })
}
// steps info --end

// prefetchers --start
const getStepQueriesPrefetchersMap: MemoizedFn<(qc: QueryClient) => Record<EStep, TEmptyAsyncCallback>> = memoizeOne(
    (qc: QueryClient): Record<EStep, TEmptyAsyncCallback> => ({
        [EStep.Profile]: emptyAsyncCallback,
        [EStep.Video]: async (): Promise<void> =>
            await qc.prefetchQuery<IVideoStepQueryDto, AxiosError, IVideoStepQueryModel, QueryKey>({
                queryKey: EQueryKey.Steps.Video(),
                queryFn: fetchers.steps[EStep.Video],
                staleTime: minutes(5)
            }),
        [EStep._LegacyModules]: emptyAsyncCallback,
        [EStep.Diagnostic]: async (): Promise<void> =>
            await qc.prefetchQuery<IDiagnosticStepQueryDto[], AxiosError, IDiagnosticStepQueryModel[], QueryKey>({
                queryKey: EQueryKey.Steps.Diagnostic(),
                queryFn: fetchers.steps[EStep.Diagnostic],
                staleTime: minutes(5)
            }),
        [EStep._LegacyDiagnosticResponses]: emptyAsyncCallback,
        [EStep.Reflection]: async (): Promise<void> =>
            await qc.prefetchQuery<IReflectionStepQueryDto[], AxiosError, IReflectionStepQueryModel[], QueryKey>({
                queryKey: EQueryKey.Steps.Reflection(),
                queryFn: fetchers.steps[EStep.Reflection],
                staleTime: minutes(5)
            }),
        [EStep.Holds]: async (): Promise<void> =>
            await qc.prefetchQuery<IHoldsStepQueryDto, AxiosError, IHoldsStepQueryModel, QueryKey>({
                queryKey: EQueryKey.Steps.Holds(),
                queryFn: fetchers.steps[EStep.Holds],
                staleTime: minutes(5)
            }),
        [EStep.Coach]: async (): Promise<void> => {
            await qc.prefetchQuery<ICoachStepSessionQueryDto, AxiosError, ICoachStepSessionQueryModel, QueryKey>({
                queryKey: EQueryKey.Steps.Coach.Session(),
                queryFn: fetchers.steps[EStep.Coach].session,
                staleTime: minutes(5)
            })
            await qc.prefetchQuery<ICoachStepPairingQueryDto, AxiosError, ICoachStepPairingModel[], QueryKey>({
                queryKey: EQueryKey.Steps.Coach.Pairing(),
                queryFn: fetchers.steps[EStep.Coach].pairing,
                staleTime: minutes(5)
            })
            await qc.prefetchQuery<ICoachStepCoachesQueryDto, AxiosError, ICoachStepCoachesQueryModel[], QueryKey>({
                queryKey: EQueryKey.Steps.Coach.Coaches(),
                queryFn: fetchers.steps[EStep.Coach].coaches,
                staleTime: minutes(5)
            })
        },
        [EStep.Final]: emptyAsyncCallback
    })
)

// prefetchers --end

// steps --start
type TVideoStepQuery = UseSuspenseQueryResult<IVideoStepQueryModel, AxiosError>
function useVideoStepSuspenseQuery(): TVideoStepQuery {
    return useSuspenseQuery<IVideoStepQueryDto, AxiosError, IVideoStepQueryModel, QueryKey>({
        queryKey: EQueryKey.Steps.Video(),
        queryFn: fetchers.steps[EStep.Video],
        select: adapters.steps[EStep.Video],
        staleTime: minutes(5),
        gcTime: 0,
        retry: (failureCount: number, error: AxiosError): boolean =>
            isUnauthorizedError(error) ? false : failureCount < 1,
        retryDelay: seconds(5),
        refetchOnReconnect: true,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        networkMode: "online",
        notifyOnChangeProps: ["data"]
    })
}

type TDiagnosticStepQuery = UseSuspenseQueryResult<IDiagnosticStepQueryModel[], AxiosError>
function useDiagnosticStepSuspenseQuery(): TDiagnosticStepQuery {
    return useSuspenseQuery<IDiagnosticStepQueryDto[], AxiosError, IDiagnosticStepQueryModel[], QueryKey>({
        queryKey: EQueryKey.Steps.Diagnostic(),
        queryFn: fetchers.steps[EStep.Diagnostic],
        select: adapters.steps[EStep.Diagnostic],
        staleTime: minutes(5),
        gcTime: Infinity,
        retry: (failureCount: number, error: AxiosError): boolean =>
            isUnauthorizedError(error) ? false : failureCount < 1,
        retryDelay: seconds(5),
        refetchOnReconnect: true,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        networkMode: "online",
        notifyOnChangeProps: ["data"]
    })
}

type TReflectionStepQuery = UseSuspenseQueryResult<IReflectionStepQueryModel[], AxiosError>
function useReflectionStepSuspenseQuery(): TReflectionStepQuery {
    return useSuspenseQuery<IReflectionStepQueryDto[], AxiosError, IReflectionStepQueryModel[], QueryKey>({
        queryKey: EQueryKey.Steps.Reflection(),
        queryFn: fetchers.steps[EStep.Reflection],
        select: adapters.steps[EStep.Reflection],
        staleTime: minutes(5),
        gcTime: Infinity,
        retry: (failureCount: number, error: AxiosError): boolean =>
            isUnauthorizedError(error) ? false : failureCount < 1,
        retryDelay: seconds(5),
        refetchOnReconnect: true,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        networkMode: "online",
        notifyOnChangeProps: ["data"]
    })
}

type THoldsStepQuery = UseSuspenseQueryResult<IHoldsStepQueryModel, AxiosError>
function useHoldsStepSuspenseQuery(): THoldsStepQuery {
    return useSuspenseQuery<IHoldsStepQueryDto, AxiosError, IHoldsStepQueryModel, QueryKey>({
        queryKey: EQueryKey.Steps.Holds(),
        queryFn: fetchers.steps[EStep.Holds],
        select: adapters.steps[EStep.Holds],
        staleTime: minutes(5),
        gcTime: Infinity,
        retry: (failureCount: number, error: AxiosError): boolean =>
            isUnauthorizedError(error) ? false : failureCount < 1,
        retryDelay: seconds(5),
        refetchOnReconnect: true,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        networkMode: "online",
        notifyOnChangeProps: ["data"]
    })
}

type TCoachStepSessionQuery = UseSuspenseQueryResult<ICoachStepSessionQueryModel, AxiosError>
function useCoachStepSessionSuspenseQuery(): TCoachStepSessionQuery {
    return useSuspenseQuery<ICoachStepSessionQueryDto, AxiosError, ICoachStepSessionQueryModel, QueryKey>({
        queryKey: EQueryKey.Steps.Coach.Session(),
        queryFn: fetchers.steps[EStep.Coach].session,
        select: adapters.steps[EStep.Coach].session,
        staleTime: minutes(5),
        gcTime: Infinity,
        retry: (failureCount: number, error: AxiosError): boolean =>
            isUnauthorizedError(error) ? false : failureCount < 1,
        retryDelay: seconds(5),
        refetchOnReconnect: true,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        networkMode: "online",
        notifyOnChangeProps: ["data", "refetch"]
    })
}

type TCoachStepPairingQuery = UseSuspenseQueryResult<ICoachStepPairingModel[], AxiosError>
function useCoachStepPairingSuspenseQuery(): TCoachStepPairingQuery {
    return useSuspenseQuery<ICoachStepPairingQueryDto, AxiosError, ICoachStepPairingModel[], QueryKey>({
        queryKey: EQueryKey.Steps.Coach.Pairing(),
        queryFn: fetchers.steps[EStep.Coach].pairing,
        select: adapters.steps[EStep.Coach].pairing,
        staleTime: minutes(5),
        gcTime: Infinity,
        retry: (failureCount: number, error: AxiosError): boolean =>
            isUnauthorizedError(error) ? false : failureCount < 1,
        retryDelay: seconds(5),
        refetchOnReconnect: true,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        networkMode: "online",
        notifyOnChangeProps: ["data"]
    })
}

type TCoachStepCoachesQuery = UseSuspenseQueryResult<ICoachStepCoachesQueryModel[], AxiosError>
function useCoachStepCoachesSuspenseQuery(): TCoachStepCoachesQuery {
    return useSuspenseQuery<ICoachStepCoachesQueryDto, AxiosError, ICoachStepCoachesQueryModel[], QueryKey>({
        queryKey: EQueryKey.Steps.Coach.Coaches(),
        queryFn: fetchers.steps[EStep.Coach].coaches,
        select: adapters.steps[EStep.Coach].coaches,
        staleTime: minutes(5),
        gcTime: Infinity,
        retry: (failureCount: number, error: AxiosError): boolean =>
            isUnauthorizedError(error) || isNotFoundError(error) ? false : failureCount < 1,
        retryDelay: seconds(5),
        refetchOnReconnect: true,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        networkMode: "online",
        notifyOnChangeProps: ["data"]
    })
}

function participantOnboardingCoachStepCoachesQueryRefetcher({ qc }: { qc: QueryClient }): void {
    void qc.refetchQueries({ queryKey: EQueryKey.Steps.Coach.Coaches(), exact: true })
}

type TStepQueryHooksMap = {
    [EStep.Video]: [() => TVideoStepQuery]
    [EStep.Diagnostic]: [() => TDiagnosticStepQuery]
    [EStep.Reflection]: [() => TReflectionStepQuery]
    [EStep.Holds]: [() => THoldsStepQuery]
    [EStep.Coach]: [() => TCoachStepSessionQuery, () => TCoachStepPairingQuery, () => TCoachStepCoachesQuery]
}
const stepQueryHooksMap: TStepQueryHooksMap = {
    [EStep.Video]: [useVideoStepSuspenseQuery],
    [EStep.Diagnostic]: [useDiagnosticStepSuspenseQuery],
    [EStep.Reflection]: [useReflectionStepSuspenseQuery],
    [EStep.Holds]: [useHoldsStepSuspenseQuery],
    [EStep.Coach]: [
        useCoachStepSessionSuspenseQuery,
        useCoachStepPairingSuspenseQuery,
        useCoachStepCoachesSuspenseQuery
    ]
}
// steps --end

function useGetCachedParticipantOnboardingCoachStepSession(): ICoachStepSessionQueryModel {
    const qc: QueryClient = useQueryClient()

    return useMemo<ICoachStepSessionQueryModel | undefined>((): ICoachStepSessionQueryModel | undefined => {
        const queryData: ICoachStepSessionQueryDto = qc.getQueryData<ICoachStepSessionQueryDto>(
            EQueryKey.Steps.Coach.Session()
        )

        return !!queryData ? adapters.steps[EStep.Coach].session(queryData) : undefined
    }, [qc])
}

export default {
    useStepInfoQuery,
    getStepQueriesPrefetchersMap,
    steps: stepQueryHooksMap
}
export { useGetCachedParticipantOnboardingCoachStepSession }
export { participantOnboardingCoachStepCoachesQueryRefetcher }
export type {
    TStepInfoQuery as TParticipantOnboardingStepsInfoQuery,
    TVideoStepQuery as TParticipantOnboardingVideoStepSuspenseQuery,
    TDiagnosticStepQuery as TParticipantOnboardingDiagnosticStepSuspenseQuery,
    TReflectionStepQuery as TParticipantOnboardingReflectionStepSuspenseQuery,
    THoldsStepQuery as TParticipantOnboardingHoldsStepSuspenseQuery,
    TCoachStepSessionQuery as TParticipantOnboardingCoachStepSessionSuspenseQuery,
    TCoachStepPairingQuery as TParticipantOnboardingCoachStepPairingSuspenseQuery,
    TCoachStepCoachesQuery as TParticipantOnboardingCoachStepCoachesSuspenseQuery,
    TStepQueryHooksMap as TParticipantOnboardingStepQueryHooksMap
}
